import type { FacilityType } from '@stars-and-ships/api-types';
import { useCodex } from '../../api/queries';
import { BUILDING_TEXT, effectLabel, effectValue, PLANET_DEPENDENT } from '../../codex';
import { FACILITY_LABELS, formatDuration, formatNumber } from '../../labels';
import { ErrorNotice, Sheet, Spinner } from '../ui';

export function BuildingInfo({
  galaxyId,
  facility,
  currentLevel,
  open,
  onClose,
}: {
  galaxyId: string;
  facility: FacilityType;
  currentLevel?: number | undefined;
  open: boolean;
  onClose(): void;
}) {
  const codex = useCodex(galaxyId);
  const entry = codex.data?.buildings.find((b) => b.type === facility);
  return (
    <Sheet open={open} title={FACILITY_LABELS[facility]} onClose={onClose}>
      {codex.isPending ? <Spinner /> : null}
      {codex.error ? <ErrorNotice error={codex.error} /> : null}
      <p className="text-sm text-text">{BUILDING_TEXT[facility]}</p>
      {PLANET_DEPENDENT.has(facility) ? (
        <p className="mt-1 text-xs text-faint">
          Base values — a system's planets can change production.
        </p>
      ) : null}
      {entry ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-faint">
              <tr>
                <th className="py-1 pr-2">Lv</th>
                {entry.levels[0]?.effects.map((e) => (
                  <th key={e.key} className="pr-2">
                    {effectLabel(e.key, facility)}
                  </th>
                ))}
                <th className="pr-2">Cost (M / G / S)</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {entry.levels.map((l) => (
                <tr
                  key={l.level}
                  className={l.level === currentLevel ? 'text-accent' : 'text-muted'}
                >
                  <td className="py-1 pr-2">{l.level}</td>
                  {l.effects.map((e) => (
                    <td key={e.key} className="pr-2">
                      {effectValue(e.key, e.value)}
                    </td>
                  ))}
                  <td className="pr-2">
                    {formatNumber(l.cost.metal)} / {formatNumber(l.cost.gas)} /{' '}
                    {formatNumber(l.cost.supplies)}
                  </td>
                  <td>{formatDuration(l.durationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Sheet>
  );
}
